import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Grid,
  Paper,
  Divider,
  useMediaQuery,
} from "@mui/material";
import {
  People as PeopleIcon,
  Payment as PaymentIcon,
  CardMembership as CardIcon,
  Schedule as ScheduleIcon,
  FitnessCenter as FitnessIcon,
  Category as CategoryIcon,
  Dashboard as DashboardIcon,
  AttachMoney as PriceIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  MoneyOff as MoneyOffIcon,
  Queue as QueueIcon,
} from "@mui/icons-material";
import { DeleteUsers as LessonManagement } from "./LessonAttendies";
import Payments from "./components/Admin/Payments";
import UserPassesComponent from "./components/Admin/UserPassesComponent";
import ScheduledLessonManagement from "./components/Admin/ScheduledLessonManagement";
import ConstLessonManagement from "./components/Admin/ConstLessonManagement";
import LessonTypeManagement from "./components/Admin/LessonTypeManagement";
import LessonCategoryManagement from "./components/Admin/LessonCategoryManagement";
import PriceListCategoryManagement from "./components/Admin/PriceListCategoryManagement";
import PassTypeManagement from "./components/Admin/PassTypeManagement";
import DebtManagement from "./components/Admin/DebtManagement";
import WaitlistManagement from "./components/Admin/WaitlistManagement";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Admin = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isUserListOpen, setUserListOpen] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [isPassManagementOpen, setPassManagementOpen] = useState(false);
  const isXs = useMediaQuery("(max-width:600px)");

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenUserList = (lessonId) => {
    setSelectedLessonId(lessonId);
    setUserListOpen(true);
  };

  const handleOpenPassManagement = () => {
    setPassManagementOpen(true);
  };

  const adminSections = [
    {
      label: "Zarządzanie zajęciami",
      icon: <ScheduleIcon />,
      component: <ScheduledLessonManagement />
    },
    {
      label: "Stałe zajęcia",
      icon: <CalendarIcon />,
      component: <ConstLessonManagement />
    },
    {
      label: isXs ? "Uczestnicy" : "Uczestnictwo w zajęciach",
      icon: <PeopleIcon />,
      component: <LessonManagement onAddUser={handleOpenUserList} />
    },
    {
      label: "Listy oczekujących",
      icon: <QueueIcon />,
      component: <WaitlistManagement />
    },
    {
      label: "Typy zajęć",
      icon: <FitnessIcon />,
      component: <LessonTypeManagement />
    },
    {
      label: "Kategorie zajęć",
      icon: <CategoryIcon />,
      component: <LessonCategoryManagement />
    },
    {
      label: "Płatności",
      icon: <PaymentIcon />,
      component: <Payments />
    },
    {
      label: "Zarządzanie długami",
      icon: <MoneyOffIcon />,
      component: <DebtManagement />
    },
    {
      label: "Karnety użytkowników",
      icon: <CardIcon />,
      component: <UserPassesComponent />
    },
    {
      label: "Zarządzanie typami karnetów",
      icon: <SettingsIcon />,
      component: <PassTypeManagement />
    },
    {
      label: "Kategorie cennika",
      icon: <PriceIcon />,
      component: <PriceListCategoryManagement />
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#2c3e50', mb: 3 }}>
        <Toolbar>
          <DashboardIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Panel Administracyjny - Natchnienie Studio
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Welcome Card */}
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: 'white', 
                fontWeight: 'bold', 
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' }
              }}
            >
              Panel Administracyjny
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Zarządzanie studiem fitness - użytkownicy, zajęcia, płatności i karnety
            </Typography>
          </CardContent>
        </Card>

        {/* Navigation Tabs */}
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#667eea',
              },
              '& .MuiTab-root': {
                minHeight: { xs: 60, sm: 72 },
                textTransform: 'none',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 500,
                minWidth: { xs: 120, sm: 160 },
                px: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0.5, sm: 1 },
              },
              '& .Mui-selected': {
                color: '#667eea !important',
              },
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            {adminSections.map((section, index) => (
              <Tab
                key={index}
                icon={React.cloneElement(section.icon, { 
                  sx: { fontSize: { xs: '1.2rem', sm: '1.5rem' } }
                })}
                label={section.label}
                iconPosition={isXs ? "top" : "start"}
                sx={{ 
                  '& .MuiTab-iconWrapper': {
                    mb: { xs: 0.5, sm: 0 },
                  }
                }}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        {adminSections.map((section, index) => (
          <TabPanel key={index} value={tabValue} index={index}>
            <Card sx={{ minHeight: '60vh' }}>
              <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                {section.component}
              </CardContent>
            </Card>
          </TabPanel>
        ))}
      </Container>
    </Box>
  );
};

export default Admin;
